import React, { useState, useEffect, useRef, Fragment, useCallback } from 'react';
import { Button, Loading } from '@nextui-org/react';
import { StripeProvider } from 'react-stripe-elements';

import CardInput from '../../components/CardInput';
import { mstore } from '../../store';
import { error as errorNotif, success as successNotif } from '../../lib/userAlerts';
import { secureFetch } from '../../lib/api';
import endpoint from '../../config/endpoint';
import { updateTitle } from "../../lib/utils";
import logo from '../../assets/stripe.png';
import { CreditCard } from '../../icons/CreditCard';

const PaymentInformation = (props) => {
  const [holderName, setHolderName] = useState(mstore.get('user', 'name') || '');
  const [savedCards, setSavedCards] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadingCard, setLoadingCard] = useState(-1);
  const [cardValue, setCardValue] = useState(null);

  const stripeRef = useRef(null);

  // Fetch cards on mount
  useEffect(() => {
    fetchCardDetails();
    updateTitle("Payment Management");
    // eslint-disable-next-line
  }, []);

  // Fetch card details
  const fetchCardDetails = useCallback(() => {
    setFetching(true);
    secureFetch(endpoint.stripeToken)
      .then(([ok, status, res]) => {
        if (ok) {
          setSavedCards(res);
        } else {
          res.json().then(j => errorNotif(j.message))
            .catch(() => errorNotif('Opps! An error occured'));
        }
        setFetching(false);
        setLoadingCard(-1);
      }).catch(err => {
        setFetching(false);
        setLoadingCard(-1);
        errorNotif('Opps! Something is wrong');
      });
  }, []);

  // Set Stripe instance
  const setStripeWalaRef = useCallback(($ref) => {
    if ($ref) {
      setTimeout(() => {
        if (!$ref.stripe) return setStripeWalaRef($ref);
        stripeRef.current = $ref.stripe;
      }, 1000);
    }
  }, []);

  // Handle card submit
  const handleCardSubmit = useCallback(async (evt) => {
    evt.preventDefault();
    const stripe = stripeRef.current;
    if (!stripe) {
      errorNotif('Cannot add card! Please Reload and try');
      return;
    }
    const { token, error } = await stripe.createToken({ name: holderName });
    if (error) {
      errorNotif('Couldn\'t add card!');
      return;
    }
    setFetching(true);
    secureFetch(endpoint.stripeToken + '?operation=add', { token })
      .then(([ok, status, res]) => {
        if (ok) {
          fetchCardDetails();
          successNotif(res.message);
        } else {
          res.json().then(r => errorNotif(r.message));
          setFetching(false);
        }
      }).catch(() => {
        setFetching(false);
        errorNotif('Oops! Something is wrong');
      });
  }, [holderName, fetchCardDetails]);

  // Handle default card
  const handleDefault = useCallback((cd, idx) => {
    const stripe = stripeRef.current;
    if (!stripe) {
      errorNotif('Cannot add card! Please Reload and try');
      return;
    }
    setFetching(true);
    setLoadingCard(idx);
    secureFetch(endpoint.stripeToken + '?operation=set_default', { token: cd })
      .then(([ok, status, res]) => {
        if (ok) {
          fetchCardDetails();
          successNotif('Successfully updated default card');
        } else {
          setFetching(false);
          setLoadingCard(-1);
          res.json().then(r => errorNotif(r.message));
        }
      }).catch(() => {
        setFetching(false);
        setLoadingCard(-1);
        errorNotif('Opps! Something is wrong');
      });
  }, [fetchCardDetails]);

  // Delete card
  const deleteCard = useCallback((cd, idx) => {
    const stripe = stripeRef.current;
    if (!stripe) {
      errorNotif('Cannot add card! Please Reload and try');
      return;
    }
    if (savedCards.length < 2) {
      errorNotif('You have only one card left');
      return;
    }
    setFetching(true);
    setLoadingCard(idx);
    secureFetch(endpoint.stripeToken + '?operation=remove', { token: cd })
      .then(([ok, status, res]) => {
        if (ok) {
          fetchCardDetails();
          successNotif('Successfully deleted card');
        } else {
          setFetching(false);
          setLoadingCard(-1);
          res.json().then(r => errorNotif(r.message));
        }
      }).catch(() => {
        setFetching(false);
        setLoadingCard(-1);
        errorNotif('Opps! Something is wrong');
      });
  }, [fetchCardDetails, savedCards.length]);

  // Handle form ref for Stripe
  const handleStripeForm = useCallback((formRef) => {
    if (formRef) {
      formRef.addEventListener('submit', handleCardSubmit);
    }
  }, [handleCardSubmit]);

  // Render
  return (
    <div className='lg:px-32'>
      <div className="flex justify-between py-8">
        <div className="w-100-m shadow-lg p-6 bg-white" style={{ width: '600px' }}>
          <div className="flex items-center mb2">
            {fetching && <Loading size='xs' css={{ mr: 10 }} />}
            <label className="f4 fw7 mb3">Enter card details</label>
          </div>
          <CardInput
            inputFormRef={setStripeWalaRef}
            formRef={handleStripeForm}
            holderName={holderName}
            onNameChange={setHolderName}
            onCardValueChange={setCardValue}
          />
        </div>

        <div className="flex flex-column w-2/5 bg-white p-6 shadow-lg">
          <div className="flex items-center mb2">
            <label className="f4 fw7 pb-4 w-full">Saved Cards</label>
            {fetching && <Loading size='md' css={{ ml: 20 }} />}
          </div>
          <div className="flex items-center-m pt-4">
            <div className="w-100">
              <table className="f6 w-100 mw8 center" cellSpacing="0">
                <thead>
                  <tr>
                    <th className="fw6 bb b--black-20 tl pb3 pr3">Card Details</th>
                    <th className="fw6 bb b--black-20 tl pb3 pr3">Card Status</th>
                  </tr>
                </thead>
                <tbody className="lh-copy">
                  {savedCards.map((cd, idx) => (
                    <Fragment key={cd.card_last4 + idx}>
                      <tr>
                        <td className="pv3 pr3 bb b--black-20">
                          <div className='flex'>
                            <span className="f4 mr3 fw2">{cd.card_brand}</span>
                            <CreditCard height={24} width={24} />
                            <span className='f5 fw2 dib mh2'>XXXX-XXXX-XXXX-{cd.card_last4}</span>
                            <span className='f5 fw2 bg-washed-yellow dib i'>({cd.exp_month}/{cd.exp_year})</span>
                          </div>
                        </td>
                        <td className="pv3 pr3 bb b--black-20">
                          {cd.is_default ? (
                            <Button color='success' disabled css={{ br: 0 }} size='sm'>Default Card</Button>
                          ) : (
                            <Button
                              flat
                              size='sm'
                              css={{ br: 0 }}
                              onClick={() => handleDefault(cd, idx)}
                            >
                              {idx === loadingCard ? <Loading size='sm' /> : 'Make Default'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="py-8">
        <img src={logo} style={{ height: 30 }} alt="Stripe logo" />
      </div>
    </div>
  );
};

export default props => (
  <StripeProvider apiKey={process.env.REACT_APP_STRIPE_APIKEY}>
    <PaymentInformation {...props} />
  </StripeProvider>
);